/** Edit a draft offer. */
import { useMemo, useState, type ChangeEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { OfferResponse } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { InputText } from "@primereact/ui/inputtext";
import { Textarea } from "@primereact/ui/textarea";
import { apiPatch } from "../../lib/api.js";
import { Field } from "../../components/ui/Field.js";
import { DateInput } from "../../components/ui/controls.js";
import { MutationError } from "../shared.js";
import { Panel } from "../commercial/document.js";
import {
  DiscountFields,
  LineEditor,
  TotalsPanel,
  emptyLine,
  linesAreValid,
  linesPayload,
  liveTotals,
  toEditableLines,
  toTotalsLike,
  useCommercialSettings,
  type EditableLine,
} from "../commercial/lines.js";

export function OfferEditor({ offer }: { offer: OfferResponse }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useCommercialSettings();
  const [title, setTitle] = useState(offer.title);
  const [introText, setIntroText] = useState(offer.introText ?? "");
  const [termsText, setTermsText] = useState(offer.termsText ?? "");
  const [customerNotes, setCustomerNotes] = useState(offer.customerNotes ?? "");
  const [internalNotes, setInternalNotes] = useState(offer.internalNotes ?? "");
  const [validUntil, setValidUntil] = useState(offer.validUntil ?? "");
  const [discount, setDiscount] = useState({
    percent: offer.discountPercent ?? "",
    amount: offer.discountAmount ?? "",
  });
  const [lines, setLines] = useState<EditableLine[]>(() =>
    offer.items.length
      ? toEditableLines(offer.items)
      : [emptyLine(settings.data?.defaultTaxRate)],
  );
  const [dirty, setDirty] = useState(false);
  const touch =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setDirty(true);
    };
  const totals = useMemo(
    () => liveTotals(lines, discount, settings.data),
    [lines, discount, settings.data],
  );
  const save = useMutation({
    mutationFn: () =>
      apiPatch<{ data: OfferResponse }>(`/offers/${offer.id}`, {
        version: offer.version,
        title: title.trim(),
        introText,
        termsText,
        customerNotes,
        internalNotes,
        validUntil: validUntil || null,
        discountPercent: discount.percent.trim() || null,
        discountAmount: discount.amount.trim() || null,
        items: linesPayload(lines),
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(["offer", offer.id], response.data);
      void queryClient.invalidateQueries({ queryKey: ["offers"] });
      setDirty(false);
    },
  });
  const valid = title.trim() && linesAreValid(lines);
  const showTax = settings.data?.vatRegistered !== false;

  return (
    <form
      id="offer-editor"
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <Panel>
        <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
          <Field label={t("common.title")} required>
            <InputText
              value={title}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                touch(setTitle)(event.target.value)
              }
            />
          </Field>
          <Field label={t("offer.validUntil")}>
            <DateInput value={validUntil} onChange={touch(setValidUntil)} />
          </Field>
        </div>
        <Field label={t("doc.introText")} className="mt-4">
          <Textarea
            value={introText}
            rows={2}
            autoResize
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              touch(setIntroText)(event.target.value)
            }
          />
        </Field>
      </Panel>
      <Panel title={t("lines.heading")}>
        <LineEditor
          lines={lines}
          onChange={touch(setLines)}
          currency={offer.currency}
          settings={settings.data}
        />
      </Panel>
      <Panel title={t("totals.heading")}>
        <div className="grid gap-6 md:grid-cols-2">
          <DiscountFields
            percent={discount.percent}
            amount={discount.amount}
            onChange={touch(setDiscount)}
            currency={offer.currency}
          />
          <TotalsPanel
            totals={toTotalsLike(totals)}
            currency={offer.currency}
            showTax={showTax}
          />
        </div>
      </Panel>
      <Panel title={t("doc.texts")}>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label={t("doc.termsText")}>
            <Textarea
              value={termsText}
              rows={3}
              autoResize
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                touch(setTermsText)(event.target.value)
              }
            />
          </Field>
          <Field label={t("doc.customerNotes")}>
            <Textarea
              value={customerNotes}
              rows={3}
              autoResize
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                touch(setCustomerNotes)(event.target.value)
              }
            />
          </Field>
          <Field label={t("doc.internalNotes")} hint={t("doc.internalHint")}>
            <Textarea
              value={internalNotes}
              rows={2}
              autoResize
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                touch(setInternalNotes)(event.target.value)
              }
            />
          </Field>
        </div>
      </Panel>
      <MutationError error={save.error} />
      <div className="sticky bottom-16 z-10 flex items-center justify-between gap-3 rounded-xl border border-app-border bg-app-surface/95 px-4 py-3 shadow-sm backdrop-blur md:bottom-0">
        <span className="text-sm text-app-muted">
          {dirty ? t("doc.unsaved") : t("doc.saved")}
        </span>
        <Button
          type="submit"
          loading={save.isPending || undefined}
          disabled={!valid || !dirty}
        >
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
}
